import React from "react";

const ROW_WIDTHS = ["w-4/5", "w-2/3", "w-3/4", "w-1/2"];

const lineWidth = (index) => ROW_WIDTHS[index % ROW_WIDTHS.length];

const SkeletonLine = ({ className = "" }) => (
    <span
        aria-hidden="true"
        className={`block rounded-full bg-forest/10 ${className || "h-3 w-full"}`}
    />
);

const TableFrame = ({ columns, headers, children }) => (
    <div className="overflow-hidden rounded-card border border-border-card bg-white shadow-sm">
        <div
            className="grid bg-forest px-3 py-2.5"
            style={{ gridTemplateColumns: columns }}
        >
            {headers.map((width, index) => (
                <SkeletonLine
                    key={index}
                    className={`h-2.5 bg-white/20 ${width}`}
                />
            ))}
        </div>
        <div className="divide-y divide-[#efece2]">{children}</div>
    </div>
);

const StandingsSkeleton = () => (
    <TableFrame
        columns="minmax(180px,2fr) 70px 70px 70px 110px"
        headers={["w-14", "w-8", "w-10", "w-8", "w-20"]}
    >
        {Array.from({ length: 8 }).map((_, rowIndex) => (
            <div
                key={rowIndex}
                className="grid items-center gap-3 px-3 py-3"
                style={{ gridTemplateColumns: "minmax(180px,2fr) 70px 70px 70px 110px" }}
            >
                <div className="space-y-1.5">
                    <SkeletonLine className={`h-3.5 ${lineWidth(rowIndex)}`} />
                    <SkeletonLine className="h-2.5 w-1/3 bg-forest/5" />
                </div>
                <SkeletonLine className="h-3 w-8" />
                <SkeletonLine className="h-3 w-8" />
                <SkeletonLine className="h-3 w-8" />
                <SkeletonLine className="h-3 w-16" />
            </div>
        ))}
    </TableFrame>
);

const PlayoffTableSkeleton = ({ variant }) => {
    const configs = {
        "playoff-odds": {
            columns: "minmax(150px,1.4fr) minmax(130px,1.2fr) repeat(5,minmax(82px,1fr))",
            headers: ["w-14", "w-14", "w-10", "w-10", "w-8", "w-14", "w-16"],
        },
        "rank-distribution": {
            columns: "minmax(150px,1.4fr) minmax(130px,1.2fr) repeat(7,minmax(72px,1fr))",
            headers: ["w-14", "w-14", "w-8", "w-8", "w-8", "w-8", "w-8", "w-8", "w-16"],
        },
        "seeding-outcomes": {
            columns: "minmax(150px,1.4fr) minmax(130px,1.2fr) repeat(5,minmax(96px,1fr))",
            headers: ["w-14", "w-14", "w-12", "w-12", "w-14", "w-12", "w-12"],
        },
        "remaining-schedule": {
            columns: "minmax(150px,1.4fr) minmax(130px,1.2fr) repeat(4,minmax(110px,1fr))",
            headers: ["w-14", "w-14", "w-16", "w-14", "w-16", "w-16"],
        },
    };
    const { columns, headers } = configs[variant] || configs["playoff-odds"];

    return (
        <div className="rounded-[22px] border border-[rgba(38,74,59,0.12)] bg-[#fffdf9] p-5 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-4">
                <SkeletonLine className="h-7 w-52" />
                <span className="h-8 w-8 rounded-md bg-forest/10" />
            </div>
            <TableFrame columns={columns} headers={headers}>
                {Array.from({ length: 8 }).map((_, rowIndex) => (
                    <div
                        key={rowIndex}
                        className="grid items-center gap-3 px-3 py-3"
                        style={{ gridTemplateColumns: columns }}
                    >
                        {headers.map((_, cellIndex) => (
                            <SkeletonLine
                                key={cellIndex}
                                className={
                                    cellIndex < 2
                                        ? `h-3.5 ${cellIndex === 0 ? lineWidth(rowIndex) : "w-3/4"}`
                                        : `mx-auto h-3 ${cellIndex % 3 === 0 ? "w-14" : "w-10"}`
                                }
                            />
                        ))}
                    </div>
                ))}
            </TableFrame>
            <div className="mt-5 space-y-2">
                <SkeletonLine className="h-3 w-64" />
                <SkeletonLine className="h-3 w-full bg-forest/5" />
                <SkeletonLine className="h-3 w-4/5 bg-forest/5" />
            </div>
        </div>
    );
};

const PowerSkeleton = () => (
    <TableFrame
        columns="44px minmax(180px,2fr) 110px minmax(130px,1fr)"
        headers={["w-5", "w-14", "w-20", "w-24"]}
    >
        {Array.from({ length: 8 }).map((_, rowIndex) => (
            <div
                key={rowIndex}
                className="grid items-center gap-3 px-3 py-3"
                style={{ gridTemplateColumns: "44px minmax(180px,2fr) 110px minmax(130px,1fr)" }}
            >
                <span className="h-7 w-7 rounded-full bg-mustard/20" />
                <div className="space-y-1.5">
                    <SkeletonLine className={`h-3.5 ${lineWidth(rowIndex)}`} />
                    <SkeletonLine className="h-2.5 w-1/2 bg-forest/5" />
                </div>
                <SkeletonLine className="h-3 w-16" />
                <span className="h-6 w-20 rounded-full bg-green-light/70" />
            </div>
        ))}
    </TableFrame>
);

const LuckSkeleton = () => (
    <TableFrame
        columns="44px minmax(160px,1.3fr) minmax(220px,2fr) 90px 36px"
        headers={["w-5", "w-14", "w-32", "w-16", "w-4"]}
    >
        {Array.from({ length: 8 }).map((_, rowIndex) => (
            <div
                key={rowIndex}
                className="grid items-center gap-3 px-3 py-3"
                style={{ gridTemplateColumns: "44px minmax(160px,1.3fr) minmax(220px,2fr) 90px 36px" }}
            >
                <span className="h-7 w-7 rounded-full bg-mustard/20" />
                <div className="space-y-1.5">
                    <SkeletonLine className={`h-3.5 ${lineWidth(rowIndex)}`} />
                    <SkeletonLine className="h-2.5 w-1/2 bg-forest/5" />
                </div>
                <div className="flex items-center gap-2">
                    <span className="relative h-4 flex-1 rounded bg-bg">
                        <span className="absolute inset-y-[-2px] left-1/2 w-px bg-border" />
                        <span
                            className="absolute top-1 h-2 rounded bg-green/30"
                            style={{
                                left: rowIndex % 2 === 0 ? "50%" : "auto",
                                right: rowIndex % 2 === 0 ? "auto" : "50%",
                                width: `${24 + (rowIndex % 4) * 6}%`,
                            }}
                        />
                    </span>
                    <SkeletonLine className="h-3 w-12" />
                </div>
                <span className="h-6 w-20 rounded-full bg-green-light/70" />
                <SkeletonLine className="h-3 w-3" />
            </div>
        ))}
    </TableFrame>
);

const AwardsSkeleton = () => (
    <div className="overflow-hidden rounded-[8px] border border-border-card bg-white shadow-[0_4px_16px_rgba(31,56,41,0.12)]">
        <div className="grid grid-cols-[1fr_1px_1fr]">
            <div className="flex items-center gap-3 rounded-tl-[7px] bg-[linear-gradient(180deg,#1f3829,#172b20)] px-[1.1rem] py-[0.72rem]">
                <SkeletonLine className="h-2.5 w-20 bg-[#fffaf2]/35" />
                <span className="h-px flex-1 bg-[#fffaf2]/20" />
            </div>
            <span className="bg-border-card" />
            <div className="flex items-center gap-3 rounded-tr-[7px] bg-[linear-gradient(180deg,#1f3829,#172b20)] px-[1.1rem] py-[0.72rem]">
                <SkeletonLine className="h-2.5 w-24 bg-[#fffaf2]/35" />
                <span className="h-px flex-1 bg-[#fffaf2]/20" />
            </div>
        </div>
        <div>
            {Array.from({ length: 10 }).map((_, rowIndex) => (
                <React.Fragment key={rowIndex}>
                    {rowIndex === 7 && (
                        <div className="my-[0.4rem] h-0.5 bg-mustard" />
                    )}
                    <div
                        className={`grid grid-cols-[1fr_1px_1fr] border-b border-[#f4f1e8] ${rowIndex % 2 === 0 ? "bg-white" : "bg-[#fdfcf8]"}`}
                    >
                        <div className="grid min-w-0 grid-cols-3 items-center gap-3 px-3 py-[0.58rem]">
                            <SkeletonLine className="mx-auto h-2.5 w-20 bg-forest/10" />
                            <SkeletonLine className={`mx-auto h-3.5 ${lineWidth(rowIndex)}`} />
                            <div className="flex items-center justify-center gap-1.5">
                                <SkeletonLine className="h-3 w-12" />
                                {rowIndex % 4 === 0 && (
                                    <span className="h-4 w-7 rounded border border-mustard bg-mustard-light" />
                                )}
                            </div>
                        </div>
                        <span className="bg-border-card" />
                        <div className="grid min-w-0 grid-cols-3 items-center gap-3 px-3 py-[0.58rem]">
                            <SkeletonLine className="mx-auto h-2.5 w-20 bg-forest/10" />
                            <SkeletonLine className={`mx-auto h-3.5 ${lineWidth(rowIndex + 1)}`} />
                            <div className="flex items-center justify-center gap-1.5">
                                <SkeletonLine className="h-3 w-12" />
                                {rowIndex % 5 === 0 && (
                                    <span className="h-4 w-7 rounded border border-mustard bg-mustard-light" />
                                )}
                            </div>
                        </div>
                    </div>
                </React.Fragment>
            ))}
        </div>
    </div>
);

const MatchupCardSkeleton = ({ index }) => (
    <article className="overflow-hidden rounded-[18px] border border-[rgba(38,74,59,0.14)] bg-white shadow-sm">
        <header className="flex items-center justify-between gap-3 border-b border-[rgba(38,74,59,0.1)] bg-[#fbf8f1] px-4 py-3">
            <SkeletonLine className="h-2.5 w-24" />
            <div className="flex gap-2">
                <span className="h-5 w-16 rounded-full bg-mustard/20" />
                <span className="h-5 w-12 rounded-full bg-green/10" />
            </div>
        </header>
        <div className="space-y-3 px-4 py-4">
            {[0, 1].map((row) => (
                <div
                    key={row}
                    className="grid grid-cols-[28px_minmax(0,1fr)_86px] items-center gap-3"
                >
                    <span className={`h-7 w-7 rounded-full ${row === 0 ? "bg-green/15" : "bg-coral/15"}`} />
                    <div className="space-y-1.5">
                        <SkeletonLine className={`h-3.5 ${lineWidth(index + row)}`} />
                        <SkeletonLine className="h-2.5 w-1/2 bg-forest/5" />
                    </div>
                    <div className="space-y-1.5">
                        <SkeletonLine className="ml-auto h-4 w-14" />
                        <span className="block h-1.5 rounded-full bg-forest/10">
                            <span
                                className="block h-full rounded-full bg-green/25"
                                style={{ width: row === 0 ? "80%" : "55%" }}
                            />
                        </span>
                    </div>
                </div>
            ))}
        </div>
        <footer className="flex items-center justify-between border-t border-[rgba(38,74,59,0.08)] bg-[#fdfbf6] px-4 py-3">
            <SkeletonLine className="h-3 w-24" />
            <SkeletonLine className="h-3 w-12" />
        </footer>
    </article>
);

const BoxScoresSkeleton = () => (
    <div className="grid gap-4 lg:grid-cols-2">
        {Array.from({ length: 4 }).map((_, index) => (
            <MatchupCardSkeleton key={index} index={index} />
        ))}
    </div>
);

const NaughtySkeleton = () => (
    <div className="grid w-full items-start gap-5 lg:grid-cols-[minmax(0,1.7fr)_minmax(430px,1fr)] xl:grid-cols-[minmax(0,1.55fr)_minmax(470px,1fr)]">
        <section className="rounded-[24px] border border-[rgba(38,74,59,0.12)] bg-[rgba(255,253,249,0.96)] p-5 shadow-sm">
            <div className="mb-4 flex items-start justify-between gap-4">
                <div className="space-y-2">
                    <SkeletonLine className="h-2.5 w-36" />
                    <SkeletonLine className="h-8 w-44" />
                </div>
                <span className="h-8 w-20 rounded-full bg-forest/10" />
            </div>
            <div className="mb-4 grid gap-3 md:grid-cols-3">
                {Array.from({ length: 3 }).map((_, index) => (
                    <div key={index} className="rounded-[18px] border border-[rgba(38,74,59,0.12)] bg-[#faf6ee] p-4">
                        <SkeletonLine className="mb-3 h-2.5 w-24" />
                        <SkeletonLine className="h-7 w-10" />
                    </div>
                ))}
            </div>
            <div className="grid gap-3">
                {Array.from({ length: 4 }).map((_, index) => (
                    <article
                        key={index}
                        className="grid gap-3 rounded-[20px] border border-[rgba(197,109,78,0.14)] bg-[rgba(197,109,78,0.06)] p-3 lg:grid-cols-[110px_minmax(0,1fr)]"
                    >
                        <div className="space-y-2">
                            <SkeletonLine className="h-3.5 w-20" />
                            {index % 2 === 0 && (
                                <span className="block h-5 w-24 rounded-full bg-coral/10" />
                            )}
                        </div>
                        <div className="space-y-3">
                            <SkeletonLine className={`h-5 ${lineWidth(index)}`} />
                            <div className="flex gap-2">
                                <span className="h-6 w-20 rounded-full bg-white/80" />
                                <span className="h-6 w-28 rounded-full bg-white/80" />
                            </div>
                        </div>
                    </article>
                ))}
            </div>
        </section>
        <aside className="rounded-[20px] border border-[rgba(38,74,59,0.12)] bg-[rgba(115,131,111,0.08)] p-4">
            <SkeletonLine className="mb-2 h-2.5 w-40" />
            <SkeletonLine className="mb-4 h-5 w-56" />
            <div className="grid gap-2.5">
                {Array.from({ length: 5 }).map((_, index) => (
                    <div
                        key={index}
                        className="grid grid-cols-[minmax(0,1fr)_70px_80px] items-center gap-2 rounded-2xl border border-[rgba(38,74,59,0.12)] bg-[#fffaf2] px-3.5 py-3"
                    >
                        <SkeletonLine className={`h-3.5 ${lineWidth(index)}`} />
                        <SkeletonLine className="mx-auto h-3.5 w-7" />
                        <SkeletonLine className="mx-auto h-3.5 w-7" />
                    </div>
                ))}
            </div>
        </aside>
    </div>
);

const MatrixSkeleton = () => (
    <div className="rounded-[22px] border border-[rgba(38,74,59,0.12)] bg-[#fffdf9] p-5 shadow-sm">
        <div className="mb-4 space-y-2">
            <SkeletonLine className="h-2.5 w-28" />
            <SkeletonLine className="h-6 w-64" />
        </div>
        <TableFrame
            columns="minmax(160px,1.4fr) repeat(6,minmax(72px,1fr)) 90px"
            headers={["w-14", "w-6", "w-6", "w-6", "w-6", "w-8", "w-5", "w-12"]}
        >
            {Array.from({ length: 8 }).map((_, rowIndex) => (
                <div
                    key={rowIndex}
                    className="grid items-center gap-2 px-3 py-3"
                    style={{ gridTemplateColumns: "minmax(160px,1.4fr) repeat(6,minmax(72px,1fr)) 90px" }}
                >
                    <div className="space-y-1.5">
                        <SkeletonLine className={`h-3.5 ${lineWidth(rowIndex)}`} />
                        <SkeletonLine className="h-2.5 w-1/2 bg-forest/5" />
                    </div>
                    {Array.from({ length: 6 }).map((_, cellIndex) => (
                        <span
                            key={cellIndex}
                            className="h-9 rounded bg-mustard/10"
                            style={{ opacity: 0.35 + ((rowIndex + cellIndex) % 4) * 0.12 }}
                        />
                    ))}
                    <SkeletonLine className="h-3 w-14" />
                </div>
            ))}
        </TableFrame>
    </div>
);

const TeamCardsSkeleton = () => (
    <div className="rounded-[22px] border border-[rgba(38,74,59,0.12)] bg-[#fffdf9] p-5 shadow-sm">
        <div className="mb-4">
            <SkeletonLine className="h-6 w-44" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
                <article key={index} className="rounded-[20px] border border-[rgba(38,74,59,0.12)] bg-white p-4">
                    <div className="mb-4 flex items-start justify-between gap-3">
                        <div className="space-y-2">
                            <SkeletonLine className={`h-4 ${lineWidth(index)}`} />
                            <SkeletonLine className="h-3 w-24 bg-forest/5" />
                        </div>
                        <SkeletonLine className="h-7 w-16" />
                    </div>
                    <SkeletonLine className="mb-4 h-3 w-full" />
                    <div className="mb-3 flex h-4 overflow-hidden rounded-full bg-bg">
                        {[28, 20, 18, 14, 12, 8].map((width, segmentIndex) => (
                            <span
                                key={segmentIndex}
                                className="h-full"
                                style={{
                                    width: `${width}%`,
                                    backgroundColor: ["#2d6a4f", "#52b788", "#d4a520", "#c94f3a", "#8fbc8f", "#9ca3af"][segmentIndex],
                                    opacity: 0.22,
                                }}
                            />
                        ))}
                    </div>
                    <div className="mb-4 grid grid-cols-3 gap-2">
                        {Array.from({ length: 6 }).map((_, chipIndex) => (
                            <span key={chipIndex} className="h-5 rounded-full bg-bg" />
                        ))}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        {Array.from({ length: 3 }).map((_, calloutIndex) => (
                            <div key={calloutIndex} className="rounded-lg bg-bg p-2">
                                <SkeletonLine className="mb-1 h-3 w-full" />
                                <SkeletonLine className="h-2 w-2/3 bg-forest/5" />
                            </div>
                        ))}
                    </div>
                </article>
            ))}
        </div>
    </div>
);

const SKELETONS = {
    awards: <AwardsSkeleton />,
    standings: <StandingsSkeleton />,
    boxscores: <BoxScoresSkeleton />,
    power: <PowerSkeleton />,
    luck: <LuckSkeleton />,
    naughty: <NaughtySkeleton />,
    matrix: <MatrixSkeleton />,
    cards: <TeamCardsSkeleton />,
    "playoff-odds": <PlayoffTableSkeleton variant="playoff-odds" />,
    "rank-distribution": <PlayoffTableSkeleton variant="rank-distribution" />,
    "seeding-outcomes": <PlayoffTableSkeleton variant="seeding-outcomes" />,
    "remaining-schedule": <PlayoffTableSkeleton variant="remaining-schedule" />,
};

const LeagueTabSkeleton = ({ variant = "standings", label = "Loading tab data" }) => (
    <div
        className="animate-pulse"
        aria-busy="true"
        aria-label={label}
        role="status"
    >
        {SKELETONS[variant] || SKELETONS.standings}
    </div>
);

export default LeagueTabSkeleton;
